/**
 * KAN-1672 — reklamace doručené objednávky.
 *
 * Rozhodnutí vlastníka 2026-09-29: „Reklamovat" u doručené objednávky →
 * zákazník vybere položku(y), popíše vadu, přiloží fotky. Jestli tlačítko
 * nabídnout, říká server (``available_actions`` obsahuje ``claim`` — doručeno
 * a v reklamační lhůtě obchodu); tady se stav ani lhůta nepočítá.
 * Podané reklamace se ukazují i se stavem, ať zákazník ví, kde je.
 *
 * KAN-1736 — typ vady vybírá zákazník z platformního katalogu
 * (``GET /defect-types``, popisky v jazyce stránky); popis zůstává volný.
 *
 * KAN-1866 — u vybrané položky zákazník řekne, kolik kusů z objednaných je
 * vadných (výchozí = všechny). Objednané množství = Σ ``quantity`` řádků
 * položky; mez hlídá i server (422 ``claim_quantity_invalid``).
 */

import { useState, type FormEvent, type JSX } from "react";
import { useTranslation } from "react-i18next";

import {
  defectTypeLabel,
  useCreateOrderClaim,
  useDefectTypes,
  useOrderClaims,
} from "@/api/queries/orders";
import { ApiError } from "@/lib/api";

const MAX_PHOTOS = 5;

/** Objednané kusy položky = součet jejích řádků; 0 = neznámé. */
function orderedQuantity(item: { lines: readonly { quantity: number }[] }): number {
  return item.lines.reduce((sum, line) => sum + line.quantity, 0);
}

export function CustomerOrderClaims({
  orderNumber,
  actions,
  items,
}: {
  orderNumber: string;
  actions: readonly string[];
  items: readonly { slug: string; file_name: string; lines: readonly { quantity: number }[] }[];
}): JSX.Element | null {
  const { t, i18n } = useTranslation("order");
  const canClaim = actions.includes("claim");
  const claims = useOrderClaims(orderNumber);
  const [open, setOpen] = useState(false);
  const defectTypes = useDefectTypes({ enabled: open });
  const [picked, setPicked] = useState<string[]>([]);
  const [quantities, setQuantities] = useState<Record<string, number>>({});
  const [defectType, setDefectType] = useState("");
  const [description, setDescription] = useState("");
  const [photos, setPhotos] = useState<File[]>([]);
  const [sent, setSent] = useState(false);
  const create = useCreateOrderClaim({
    onSuccess: () => {
      setOpen(false);
      setSent(true);
      setPicked([]);
      setQuantities({});
      setDefectType("");
      setDescription("");
      setPhotos([]);
    },
  });

  const existing = claims.data ?? [];
  if (!canClaim && existing.length === 0) return null;

  const errorCode = create.error instanceof ApiError ? create.error.code : null;
  const toggle = (slug: string) =>
    setPicked((cur) => (cur.includes(slug) ? cur.filter((x) => x !== slug) : [...cur, slug]));
  const quantityFor = (item: (typeof items)[number]) =>
    quantities[item.slug] ?? (orderedQuantity(item) || 1);
  const submit = (e: FormEvent) => {
    e.preventDefault();
    create.mutate({
      orderNumber,
      itemSlugs: picked,
      quantities: picked.map((slug) => {
        const item = items.find((i) => i.slug === slug);
        return item ? quantityFor(item) : 1;
      }),
      defectType,
      description: description.trim(),
      photos,
    });
  };

  return (
    <section className="space-y-3 border-t border-border pt-3" data-testid="order-claims">
      {existing.length > 0 && (
        <div className="space-y-1.5">
          <h3 className="text-sm font-semibold text-ink">{t("claim.listTitle")}</h3>
          <ul className="space-y-1 text-sm">
            {existing.map((c) => (
              <li key={c.id} className="flex flex-wrap gap-2" data-testid={`order-claim-${c.id}`}>
                <span className="text-ink-2">
                  {c.items.map((i) => i.file_name ?? i.slug).join(", ")}
                </span>
                <span className="font-medium text-ink" data-status={c.status}>
                  {t(`claim.status.${c.status}`)}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
      {sent && (
        <p className="text-sm text-ink-2" role="status" data-testid="order-claim-sent">
          {t("claim.sent")}
        </p>
      )}
      {canClaim && !open && (
        <button
          type="button"
          className="text-sm text-ink-2 underline hover:text-ink"
          onClick={() => {
            create.reset();
            setSent(false);
            setOpen(true);
          }}
          data-testid="order-claim-open"
        >
          {t("claim.action")}
        </button>
      )}
      {canClaim && open && (
        <form className="space-y-3" onSubmit={submit} data-testid="order-claim-form">
          <p className="text-sm text-ink-2">{t("claim.lead")}</p>
          <fieldset className="space-y-1">
            <legend className="text-sm font-medium text-ink">{t("claim.items")}</legend>
            {items.map((item) => {
              const ordered = orderedQuantity(item);
              return (
                <div key={item.slug} className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm">
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={picked.includes(item.slug)}
                      onChange={() => toggle(item.slug)}
                      data-testid={`order-claim-item-${item.slug}`}
                    />
                    {item.file_name}
                  </label>
                  {picked.includes(item.slug) && ordered > 1 && (
                    <label className="flex items-center gap-2 text-ink-2">
                      {t("claim.quantity", { max: ordered })}
                      <input
                        type="number"
                        className="w-20 rounded border border-border px-2 py-1"
                        min={1}
                        max={ordered}
                        step={1}
                        required
                        value={quantityFor(item)}
                        onChange={(e) =>
                          setQuantities((cur) => ({ ...cur, [item.slug]: Number(e.target.value) }))
                        }
                        data-testid={`order-claim-quantity-${item.slug}`}
                      />
                    </label>
                  )}
                </div>
              );
            })}
          </fieldset>
          <label className="block text-sm">
            <span className="font-medium text-ink">{t("claim.defectType")}</span>
            <select
              className="mt-1 block w-full rounded border border-border px-2 py-1.5"
              required
              value={defectType}
              onChange={(e) => setDefectType(e.target.value)}
              data-testid="order-claim-defect-type"
            >
              <option value="">{t("claim.defectTypePlaceholder")}</option>
              {(defectTypes.data ?? []).map((dt) => (
                <option key={dt.code} value={dt.code}>
                  {defectTypeLabel(dt, i18n.language)}
                </option>
              ))}
            </select>
          </label>
          <label className="block text-sm">
            <span className="font-medium text-ink">{t("claim.description")}</span>
            <textarea
              className="mt-1 block w-full rounded border border-border px-2 py-1.5"
              rows={4}
              maxLength={4000}
              required
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              data-testid="order-claim-description"
            />
          </label>
          <label className="block text-sm">
            <span className="font-medium text-ink">{t("claim.photos", { max: MAX_PHOTOS })}</span>
            <input
              type="file"
              accept="image/png,image/jpeg,image/webp"
              multiple
              className="mt-1 block text-sm"
              onChange={(e) => setPhotos(Array.from(e.target.files ?? []).slice(0, MAX_PHOTOS))}
              data-testid="order-claim-photos"
            />
          </label>
          {create.isError && (
            <p className="text-sm text-danger" role="alert">
              {t(`claim.errors.${errorCode ?? "generic"}`, { defaultValue: t("claim.errors.generic") })}
            </p>
          )}
          <div className="flex gap-3">
            <button
              type="submit"
              className="rounded bg-ink px-3 py-1.5 text-sm font-medium text-white disabled:opacity-50"
              disabled={
                create.isPending || picked.length === 0 || !defectType || !description.trim()
              }
              data-testid="order-claim-submit"
            >
              {t("claim.submit")}
            </button>
            <button
              type="button"
              className="text-sm text-ink-2 underline"
              onClick={() => setOpen(false)}
            >
              {t("claim.cancel")}
            </button>
          </div>
        </form>
      )}
    </section>
  );
}
